import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Logo } from "../../components/Logo";
import { SiteFooter } from "../../components/SiteFooter";
import { api, extractErrorMessage } from "../../lib/api";

interface ResendResponse {
  message?: string;
}

export function Pending() {
  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  function openModal() {
    setMsg(null);
    setModalError(null);
    setEmail("");
    setPassword("");
    setModalOpen(true);
  }

  function closeModal() {
    if (submitting) return;
    setModalOpen(false);
  }

  // Close on Escape, mirroring the dashboard Modal's behavior. Don't close
  // mid-submit, matching closeModal().
  useEffect(() => {
    if (!modalOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !submitting) setModalOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, submitting]);

  async function handleResend(e: FormEvent) {
    e.preventDefault();
    setModalError(null);
    if (!email.trim() || !password) {
      setModalError("Enter your email and password.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post<ResendResponse>("/api/auth/resend-verification", {
        email: email.trim(),
        password,
      });
      setMsg({
        kind: "success",
        text: res.data.message ?? "Verification email sent!",
      });
      setModalOpen(false);
    } catch (err) {
      // Keep the modal open so the user can fix credentials and retry.
      setModalError(extractErrorMessage(err, "Could not resend email."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="top-line" />
      <div className="page">
        <div className="card">
          <Logo />
          <div className="icon-circle" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
              />
            </svg>
          </div>
          <h1>Check your email</h1>
          <p className="sub">
            We&apos;ve sent a verification link to your email address. Click the link to verify
            your account and start using SEILAN.
          </p>
          <p className="pw-hint" style={{ textAlign: "center", marginTop: 20 }}>
            Didn&apos;t receive the email? Check your spam folder or click below to resend.
          </p>
          <button
            type="button"
            className="btn--outline"
            onClick={openModal}
            disabled={submitting}
          >
            Resend verification email
          </button>
          {msg && (
            <p
              className={`msg msg--${msg.kind}`}
              role={msg.kind === "error" ? "alert" : "status"}
            >
              {msg.text}
            </p>
          )}
          <Link to="/login" className="back-link">
            Back to sign in
          </Link>
        </div>
      </div>
      <SiteFooter />

      {modalOpen && (
        <div
          className="auth-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Resend verification email"
        >
          <form className="card card--modal" onSubmit={handleResend} noValidate>
            <h1>Resend verification</h1>
            <p className="sub">Confirm your credentials and we&apos;ll send a new link.</p>
            {modalError && (
              <div className="error-msg" role="alert">
                {modalError}
              </div>
            )}
            <div className="form-group">
              <label htmlFor="resend-email">Email</label>
              <input
                id="resend-email"
                className="form-input"
                type="email"
                placeholder="you@moatit.com"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="resend-pw">Password</label>
              <input
                id="resend-pw"
                className="form-input"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={closeModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn--primary" disabled={submitting}>
                {submitting ? "Sending..." : "Send link"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
